import { useCallback, useEffect } from "react";

import { DateTime, DurationObjectUnits } from "luxon";

import { ParamKeyValuePair, useSearchParams } from "react-router-dom";

import { luxonUtils } from "utils/luxonUtils";

export const useUrlSearchParamsDateTimeRange = (
    searchParamNameStartDateTime: string,
    searchParamNameEndDateTime: string,
    substractValue: number,
    subtractType: keyof DurationObjectUnits,
    onDateTimeRangeChanged: (
        startDateTime: string,
        endDateTime: string,
    ) => void,
) => {
    const [searchParams, setSearchParams] = useSearchParams();

    // get range from url or fallback to current date minus X time
    useEffect(() => {
        let startDateTime = luxonUtils.fromIsoWithFallback(
            searchParams.get(searchParamNameStartDateTime),
            DateTime.now(),
        );

        let endDateTime = luxonUtils.fromIsoWithFallback(
            searchParams.get(searchParamNameEndDateTime),
            DateTime.now(),
        );

        if (!startDateTime.isValid || !endDateTime.isValid) {
            endDateTime = luxonUtils.resetSecondsAndMilliseconds(
                DateTime.now(),
            );
            startDateTime = endDateTime.minus({
                [subtractType]: substractValue,
            });
        }

        onDateTimeRangeChanged(
            startDateTime.toISO() as string,
            endDateTime.toISO() as string,
        );
    }, [
        onDateTimeRangeChanged,
        searchParamNameEndDateTime,
        searchParamNameStartDateTime,
        searchParams,
        substractValue,
        subtractType,
    ]);

    // callback to call to update url with new range
    const onUpdateDateRange = useCallback(
        (startDateTime: DateTime | null, endDateTime: DateTime | null) => {
            const params = [] as ParamKeyValuePair[];

            if (startDateTime?.isValid) {
                params.push([
                    searchParamNameStartDateTime,
                    luxonUtils
                        .resetSecondsAndMilliseconds(startDateTime)
                        .toISO() as string,
                ]);
            }

            if (endDateTime?.isValid) {
                params.push([
                    searchParamNameEndDateTime,
                    luxonUtils
                        .resetSecondsAndMilliseconds(endDateTime)
                        .toISO() as string,
                ]);
            }

            setSearchParams(params);
        },
        [
            searchParamNameEndDateTime,
            searchParamNameStartDateTime,
            setSearchParams,
        ],
    );

    return {
        onUpdateDateRange,
    };
};
